import ferroManganeseImg from "@/assets/ferro-manganese.jpg";
import silicoManganeseImg from "@/assets/silico-manganese.jpg";
import quartziteImg from "@/assets/quartzite.jpg";
import coalImg from "@/assets/coal.jpg";
import billetsImg from "@/assets/billets.jpg";
import tmtBarsImg from "@/assets/tmt-bars.jpg";
import ingotsImg from "@/assets/ingots.jpg";
import ironOreImg from "@/assets/iron-ore.jpg";
import ironPelletsImg from "@/assets/iron-pellets.jpg";
import angleImg from "@/assets/angle.jpg";
import petScrapImg from "@/assets/pet-scrap.jpg";
import hdpeScrapImg from "@/assets/hdpe-scrap.jpg";
import ldpeScrapImg from "@/assets/ldpe-scrap.jpg";
import ppScrapImg from "@/assets/pp-scrap.jpg";
import pvcScrapImg from "@/assets/pvc-scrap.jpg";
import absScrapImg from "@/assets/abs-scrap.jpg";
import psScrapImg from "@/assets/ps-scrap.jpg";
import mlpScrapImg from "@/assets/mlp-scrap.jpg";

export type MaterialSpec = { label: string; value: string };

export type ComingSoonMaterial = {
  slug: string;
  name: string;
  img: string;
  shortDesc: string;
  longDesc: string;
  specs: MaterialSpec[];
  sampleImages: string[];
};

export const COMING_SOON_MATERIALS: ComingSoonMaterial[] = [
  {
    slug: "ferro-manganese",
    name: "Ferro-Manganese",
    img: ferroManganeseImg,
    shortDesc: "High & medium-carbon alloys.",
    longDesc:
      "High-carbon (HC FeMn) and medium-carbon ferro-manganese used as a deoxidizer and alloying element in carbon, alloy and stainless steel production. Available ex-works from leading South Indian smelters.",
    specs: [
      { label: "Mn", value: "70 – 78%" },
      { label: "C", value: "6 – 7.5% (HC) / 1 – 1.5% (MC)" },
      { label: "Si", value: "≤ 1.5%" },
      { label: "P", value: "≤ 0.30%" },
      { label: "S", value: "≤ 0.03%" },
      { label: "Size", value: "10–80 mm lumps" },
    ],
    sampleImages: [ferroManganeseImg],
  },
  {
    slug: "silico-manganese",
    name: "Silico-Manganese",
    img: silicoManganeseImg,
    shortDesc: "Steel deoxidation & alloying.",
    longDesc:
      "Silico-manganese (SiMn) ferroalloy used for simultaneous deoxidation and alloying in steel mills. Preferred for billet, TMT and structural steel production due to its combined Si and Mn contribution.",
    specs: [
      { label: "Mn", value: "60 – 65%" },
      { label: "Si", value: "14 – 17%" },
      { label: "C", value: "≤ 2.0%" },
      { label: "P", value: "≤ 0.20%" },
      { label: "S", value: "≤ 0.03%" },
      { label: "Size", value: "10–60 mm / 10–80 mm" },
    ],
    sampleImages: [silicoManganeseImg],
  },
  {
    slug: "quartzite",
    name: "Quartzite",
    img: quartziteImg,
    shortDesc: "Foundry-grade silica mass.",
    longDesc:
      "High-purity quartzite lumps used as a raw material in ferro-silicon, silico-manganese and silicon-metal production, and as refractory ramming mass in induction furnaces.",
    specs: [
      { label: "SiO₂", value: "≥ 98%" },
      { label: "Fe₂O₃", value: "≤ 0.10%" },
      { label: "Al₂O₃", value: "≤ 0.50%" },
      { label: "Moisture", value: "≤ 1.0%" },
      { label: "Size", value: "10–80 mm / 40–150 mm" },
      { label: "Form", value: "Hand-picked lumps" },
    ],
    sampleImages: [quartziteImg],
  },
  {
    slug: "coal",
    name: "Coal",
    img: coalImg,
    shortDesc: "Metallurgical & thermal grades.",
    longDesc:
      "Imported and domestic coal — including low-ash metallurgical coal for coke production and steam coal for captive power plants and sponge iron kilns. Quantities loadable from Paradip, Mundra, Krishnapatnam and inland sidings.",
    specs: [
      { label: "GCV", value: "5,500 – 6,500 kcal/kg" },
      { label: "Ash", value: "8 – 14%" },
      { label: "Total Moisture", value: "10 – 14%" },
      { label: "Sulphur", value: "≤ 0.6%" },
      { label: "Volatile Matter", value: "28 – 38%" },
      { label: "Size", value: "0–50 mm" },
    ],
    sampleImages: [coalImg],
  },
  {
    slug: "billets",
    name: "Billets",
    img: billetsImg,
    shortDesc: "MS & alloy steel billets.",
    longDesc:
      "Concast mild-steel and alloy-steel billets used as feedstock for re-rolling mills producing TMT bars, wire rod, angles and structural sections. IS 2830 / 2831 grades available from induction and EAF route mills.",
    specs: [
      { label: "Grade", value: "IS 2830 / IS 2831 / 1018 / 1022" },
      { label: "Cross-section", value: "100×100, 125×125, 150×150 mm" },
      { label: "Length", value: "3 – 12 m" },
      { label: "C", value: "0.18 – 0.25%" },
      { label: "Mn", value: "0.50 – 0.90%" },
      { label: "S & P", value: "≤ 0.045% each" },
    ],
    sampleImages: [billetsImg],
  },
  {
    slug: "tmt-bars",
    name: "TMT Bars",
    img: tmtBarsImg,
    shortDesc: "High-strength reinforcement bars.",
    longDesc:
      "Thermo-mechanically treated reinforcement bars conforming to IS 1786, used for RCC construction. Fe 500, Fe 500D and Fe 550D grades available in standard and earthquake-resistant variants.",
    specs: [
      { label: "Grade", value: "Fe 500 / Fe 500D / Fe 550D" },
      { label: "Diameter", value: "8, 10, 12, 16, 20, 25, 32 mm" },
      { label: "Length", value: "12 m standard" },
      { label: "Yield Strength", value: "≥ 500 N/mm²" },
      { label: "Elongation", value: "≥ 16%" },
      { label: "Standard", value: "IS 1786:2008" },
    ],
    sampleImages: [tmtBarsImg],
  },
  {
    slug: "ingots",
    name: "Ingots",
    img: ingotsImg,
    shortDesc: "Cast iron & steel ingots.",
    longDesc:
      "Mild-steel and cast-iron ingots cast in standard moulds for re-rolling and foundry remelt applications. Available in tapered and rectangular shapes from induction furnace units.",
    specs: [
      { label: "Material", value: "MS / CI / Alloy steel" },
      { label: "Weight", value: "25 – 500 kg per ingot" },
      { label: "C", value: "0.15 – 0.30% (MS) / 3.0 – 3.6% (CI)" },
      { label: "Si", value: "0.10 – 0.35%" },
      { label: "S & P", value: "≤ 0.05% each" },
      { label: "Form", value: "Tapered / rectangular cast" },
    ],
    sampleImages: [ingotsImg],
  },
  {
    slug: "iron-ore",
    name: "Iron Ore",
    img: ironOreImg,
    shortDesc: "Hematite & magnetite fines.",
    longDesc:
      "Hematite and magnetite iron ore — fines, lumps and CLO (calibrated lump ore) — for sponge iron, blast furnace and pelletization use. Sourced from approved mines in Odisha, Jharkhand and Karnataka.",
    specs: [
      { label: "Fe", value: "58 – 65%" },
      { label: "SiO₂", value: "≤ 4.5%" },
      { label: "Al₂O₃", value: "≤ 3.5%" },
      { label: "Phosphorus", value: "≤ 0.08%" },
      { label: "Moisture", value: "≤ 4%" },
      { label: "Size", value: "Fines 0–10 mm / Lumps 10–40 mm" },
    ],
    sampleImages: [ironOreImg],
  },
  {
    slug: "iron-pellets",
    name: "Iron Pellets",
    img: ironPelletsImg,
    shortDesc: "Processed taconite pellets.",
    longDesc:
      "Iron-oxide pellets (8–18 mm) produced from beneficiated iron-ore fines — used in blast furnace and DRI sponge-iron plants for higher productivity and lower coke rate.",
    specs: [
      { label: "Fe (Total)", value: "63 – 67%" },
      { label: "FeO", value: "≤ 1.0%" },
      { label: "SiO₂ + Al₂O₃", value: "≤ 5.0%" },
      { label: "Basicity (CaO/SiO₂)", value: "0.4 – 0.6" },
      { label: "Cold Crushing Strength", value: "≥ 250 kg/pellet" },
      { label: "Size", value: "8 – 18 mm (≥ 90%)" },
    ],
    sampleImages: [ironPelletsImg],
  },
  {
    slug: "angle",
    name: "Angle",
    img: angleImg,
    shortDesc: "L-shaped structural steel.",
    longDesc:
      "Hot-rolled equal and unequal mild-steel angles conforming to IS 2062, used in fabrication, transmission towers, trusses and general structural work.",
    specs: [
      { label: "Grade", value: "IS 2062 E250 (Fe 410W)" },
      { label: "Type", value: "Equal & unequal angles" },
      { label: "Size", value: "25×25×3 mm to 200×200×20 mm" },
      { label: "Length", value: "6 – 12 m" },
      { label: "Yield Strength", value: "≥ 250 N/mm²" },
      { label: "Tensile Strength", value: "≥ 410 N/mm²" },
    ],
    sampleImages: [angleImg],
  },
  {
    slug: "pet-bottle-scrap",
    name: "PET Bottle Scrap",
    img: petScrapImg,
    shortDesc: "Baled clear & mixed PET.",
    longDesc:
      "Post-consumer PET bottle scrap collected from beverage and packaged water streams, sorted by colour and baled for flake and fibre lines. Traded per kilogram across Delhi-NCR, Mumbai, Ahmedabad and Kolkata, and fully compatible with EPR credit documentation under the Plastic Waste Management Rules.",
    specs: [
      { label: "Polymer", value: "PET (resin code 1)" },
      { label: "Form", value: "Baled bottles / washed flakes" },
      { label: "Colour sort", value: "Clear, light blue, mixed" },
      { label: "Moisture", value: "<= 1.5%" },
      { label: "Contamination", value: "<= 2% caps, labels, fines" },
      { label: "Trade unit", value: "Rs per kg" },
    ],
    sampleImages: [petScrapImg],
  },
  {
    slug: "hdpe-scrap",
    name: "HDPE Scrap",
    img: hdpeScrapImg,
    shortDesc: "Crates, drums & blow-mould.",
    longDesc:
      "Rigid high-density polyethylene scrap from crates, drums, jerry cans and blow-moulded containers. Available as loose rigids, granulated regrind or reprocessed granules for pipe, moulding and pallet manufacturers.",
    specs: [
      { label: "Polymer", value: "HDPE (resin code 2)" },
      { label: "Form", value: "Rigid baled / regrind / granules" },
      { label: "MFI", value: "0.3 - 0.9 g/10 min" },
      { label: "Moisture", value: "<= 1%" },
      { label: "Contamination", value: "<= 2% non-HDPE" },
      { label: "Trade unit", value: "Rs per kg" },
    ],
    sampleImages: [hdpeScrapImg],
  },
  {
    slug: "ldpe-film-scrap",
    name: "LDPE Film Scrap",
    img: ldpeScrapImg,
    shortDesc: "Clear & printed film bales.",
    longDesc:
      "Low-density polyethylene film scrap from liners, shrink wrap, greenhouse sheeting and industrial packaging. Graded on transparency and print load, then baled for washing and re-pelletising lines.",
    specs: [
      { label: "Polymer", value: "LDPE / LLDPE (resin code 4)" },
      { label: "Form", value: "Baled film / rolls / regrind" },
      { label: "Grade", value: "Natural clear, light print, heavy print" },
      { label: "Moisture", value: "<= 2%" },
      { label: "Bale weight", value: "250 - 450 kg" },
      { label: "Trade unit", value: "Rs per kg" },
    ],
    sampleImages: [ldpeScrapImg],
  },
  {
    slug: "pp-scrap",
    name: "PP Scrap",
    img: ppScrapImg,
    shortDesc: "Woven sacks, crates, regrind.",
    longDesc:
      "Polypropylene scrap from woven sacks and FIBC bags, injection-moulded crates, automotive bumpers and battery cases. Supplied loose, shredded or as reprocessed granules for moulders and sack extruders.",
    specs: [
      { label: "Polymer", value: "PP / PP copolymer (resin code 5)" },
      { label: "Form", value: "Bales / shredded / granules" },
      { label: "MFI", value: "4 - 12 g/10 min" },
      { label: "Ash", value: "<= 3%" },
      { label: "Moisture", value: "<= 1.5%" },
      { label: "Trade unit", value: "Rs per kg" },
    ],
    sampleImages: [ppScrapImg],
  },
  {
    slug: "pvc-scrap",
    name: "PVC Scrap",
    img: pvcScrapImg,
    shortDesc: "Rigid pipe & profile offcuts.",
    longDesc:
      "Rigid PVC scrap from pipe offcuts, window and door profiles, and extrusion rejects, plus flexible PVC from cable and flooring streams. Sorted by rigidity and filler load before pulverising.",
    specs: [
      { label: "Polymer", value: "PVC (resin code 3)" },
      { label: "Form", value: "Offcuts / pulverised powder" },
      { label: "Type", value: "Rigid (uPVC) & flexible" },
      { label: "K-value", value: "57 - 67" },
      { label: "Contamination", value: "<= 2% metal, rubber" },
      { label: "Trade unit", value: "Rs per kg" },
    ],
    sampleImages: [pvcScrapImg],
  },
  {
    slug: "abs-scrap",
    name: "ABS Scrap",
    img: absScrapImg,
    shortDesc: "E-waste & auto housings.",
    longDesc:
      "Engineering-grade ABS scrap recovered from appliance housings, electronic casings and automotive interior trim. Colour-sorted (black, grey, mixed) and available as regrind or compounded granules.",
    specs: [
      { label: "Polymer", value: "ABS / ABS-PC blends" },
      { label: "Form", value: "Regrind / granules" },
      { label: "Colour", value: "Black, grey, mixed" },
      { label: "MFI", value: "18 - 30 g/10 min" },
      { label: "Metal-free", value: "Yes, post-separation" },
      { label: "Trade unit", value: "Rs per kg" },
    ],
    sampleImages: [absScrapImg],
  },
  {
    slug: "ps-scrap",
    name: "Polystyrene (PS / EPS) Scrap",
    img: psScrapImg,
    shortDesc: "Thermocol & rigid PS.",
    longDesc:
      "Expanded polystyrene (thermocol) blocks and rigid PS scrap from packaging, disposables and appliance liners. Densified or baled at source to cut freight cost before re-extrusion.",
    specs: [
      { label: "Polymer", value: "PS / HIPS / EPS (resin code 6)" },
      { label: "Form", value: "Densified blocks / baled rigids" },
      { label: "Density (EPS)", value: "10 - 25 kg/m3 loose" },
      { label: "Moisture", value: "<= 1%" },
      { label: "Contamination", value: "<= 3% tape, film" },
      { label: "Trade unit", value: "Rs per kg" },
    ],
    sampleImages: [psScrapImg],
  },
  {
    slug: "mlp-scrap",
    name: "Multilayer Plastic (MLP) Scrap",
    img: mlpScrapImg,
    shortDesc: "Laminates for EPR & co-processing.",
    longDesc:
      "Multilayer laminated packaging scrap - printed pouches, wrappers and sachets - routed to co-processing, cement kilns and board manufacturing. The primary category brands use to close MLP obligations under EPR targets.",
    specs: [
      { label: "Polymer", value: "Multilayer laminate (PE/PET/AL)" },
      { label: "Form", value: "Baled / shredded" },
      { label: "Calorific value", value: "5,500 - 7,000 kcal/kg" },
      { label: "Moisture", value: "<= 5%" },
      { label: "End use", value: "Co-processing, board, RDF" },
      { label: "Trade unit", value: "Rs per kg" },
    ],
    sampleImages: [mlpScrapImg],
  },
];

export const getMaterialBySlug = (slug?: string) =>
  COMING_SOON_MATERIALS.find((m) => m.slug === slug);

export const buildComingSoonFaqs = (name: string, shortDesc: string) => [
  {
    q: `When will ${name} trading go live on LohaLink?`,
    a: `${name} is in the final supplier onboarding stage. Register on the Notify Me form on this page and we'll email and call you the moment ${name} listings open for buying and selling on LohaLink.`,
  },
  {
    q: `How is ${name} quality and quantity verified on LohaLink?`,
    a: `Every ${name} lot goes through GSTIN-verified supplier KYC, AI-assisted quality grading from yard / mill photos, and digital weighbridge reconciliation between outbound and inbound dharam kanta receipts — giving buyers an auditable trail before escrow release.`,
  },
  {
    q: `Will payments for ${name} be protected by escrow?`,
    a: `Yes. Every ${name} trade on LohaLink will settle through an RBI-grade nodal escrow account at a partner bank. Funds release to the seller only after the buyer accepts the inbound weighbridge and quality, eliminating bad debt and post-dated cheques.`,
  },
  {
    q: `Which Indian steel hubs will ${name} be available from?`,
    a: `LohaLink covers Durgapur, Kolkata, Jharsuguda, Raipur, Asansol, Mandi Gobindgarh, Jalna, Alang, Ghaziabad and most other major steel and foundry clusters. Loading point and freight quote for ${name} are confirmed at the time of the first enquiry.`,
  },
  {
    q: `Can I get a GST invoice and e-way bill for ${name}?`,
    a: `Yes. Tax-compliant GST invoices with IRN/QR and e-way bills are auto-generated for every ${name} trade and made available to both parties in their LohaLink dashboard. ${shortDesc}`,
  },
];
